import dayjs from 'dayjs';
import useCurrentSubscriptionHelper from 'hooks/billing/useCurrentSubscriptionHelper';
import usePlanLimits from 'hooks/plan/usePlanLimits';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonMapper } from 'utils/mapper/common.mapper';
import { useLabelCountSWR } from '../../fulfilled-by-month/hooks/useLabelCountSWR';
import { OrderStat } from '../components/order-stat-grid/OrderStatGrid';
import { useOrderOverviewStats } from './useOrderOverviewStats';

export interface OrderOverviewTiles {
    /**
     * The order overview's tiles, plus a "Monthly order limit" one for a legacy subscription and a
     * "Labels this month" one for a usage-based subscription
     */
    stats: OrderStat[];
    fulfilledLast30Days: number;
    isLoading: boolean;
}

/**
 * The tiles for `PlanOverviewDetails`' order overview: the shop-wide order counts, for a legacy
 * subscription its monthly order usage as `used/max` (only for a limited plan), and for a
 * usage-based subscription the shipping labels generated this month (same count as `LabelByMonthCount`).
 */
export const useOrderOverviewTiles = (): OrderOverviewTiles => {
    const { t } = useTranslation();
    const { isLegacy, isShopifyAppPricingLegacy, isUsageBased } = useCurrentSubscriptionHelper();
    // Both the manual-pricing legacy plans and the legacy Shopify App Pricing (`legacy-…`) plans get the monthly order limit tile
    const showMonthlyOrderLimit = isLegacy || isShopifyAppPricingLegacy;
    const {
        planLimits,
        getMonthlyOrderLimit,
        refresh: refreshPlanLimits,
        loading: isPlanLimitsLoading,
    } = usePlanLimits();
    const { ordersToFulfill, fulfilledLast30Days, totalFulfilled, isLoading } = useOrderOverviewStats();

    // The usage is only needed for the legacy "Monthly order limit" tile
    useEffect(() => {
        if (showMonthlyOrderLimit) refreshPlanLimits();
    }, [showMonthlyOrderLimit]);

    // Keyed on the limits it reads from: `getMonthlyOrderLimit` itself is a new function on every render
    const monthlyOrderLimit = useMemo(() => getMonthlyOrderLimit(), [planLimits.limits]);

    // Same range as `LabelByMonthCount`: from the start of this month to the end of today
    const labelCountFrom = useMemo(() => dayjs().startOf('month').toISOString(), []);
    const labelCountTo = useMemo(() => dayjs().add(1, 'day').startOf('day').toISOString(), []);
    const { data: labelCount, isLoading: isLabelCountLoading } = useLabelCountSWR(
        labelCountFrom,
        labelCountTo,
        isUsageBased
    );

    const stats = useMemo((): OrderStat[] => {
        const orderStats: OrderStat[] = [
            {
                label: t('DASHBOARD.PLAN_OVERVIEW_DETAILS.ORDER_OVERVIEW.ORDERS_TO_FULFILL'),
                value: ordersToFulfill,
            },
            {
                label: t('DASHBOARD.PLAN_OVERVIEW_DETAILS.ORDER_OVERVIEW.FULFILLED_LAST_30_DAYS'),
                value: fulfilledLast30Days,
            },
            {
                label: t('DASHBOARD.PLAN_OVERVIEW_DETAILS.ORDER_OVERVIEW.TOTAL_FULFILLED'),
                value: totalFulfilled,
            },
        ];

        if (showMonthlyOrderLimit) {
            const used = parseInt(monthlyOrderLimit?.currentlyUsed, 10) || 0;
            const limit = parseInt(monthlyOrderLimit?.value, 10) || 0;
            const max = limit === -1 ? t('DASHBOARD.PLAN_OVERVIEW_DETAILS.ORDER_OVERVIEW.UNLIMITED') : limit;

            orderStats.push({
                label: t('DASHBOARD.PLAN_OVERVIEW_DETAILS.ORDER_OVERVIEW.MONTHLY_ORDER_LIMIT'),
                value: `${used}/${max}`,
                isZero: used === 0,
            });
        }

        if (isUsageBased) {
            orderStats.push({
                label: t('DASHBOARD.PLAN_OVERVIEW_DETAILS.ORDER_OVERVIEW.LABELS_THIS_MONTH'),
                // `label_count` is a `uint64`
                value: CommonMapper.mapBigintIntoNumber(labelCount?.labelCount) ?? 0,
            });
        }

        return orderStats;
    }, [
        showMonthlyOrderLimit,
        ordersToFulfill,
        fulfilledLast30Days,
        totalFulfilled,
        monthlyOrderLimit?.currentlyUsed,
        monthlyOrderLimit?.value,
        isUsageBased,
        labelCount?.labelCount,
        t,
    ]);

    return {
        stats,
        fulfilledLast30Days,
        isLoading: isLoading || (showMonthlyOrderLimit && isPlanLimitsLoading) || (isUsageBased && isLabelCountLoading),
    };
};
